import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Chip,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  AccessTime as AccessTimeIcon,
  MyLocation as MyLocationIcon,
  History as HistoryIcon,
  Route as RouteIcon
} from '@mui/icons-material';

/**
 * Panel de detalles del trabajador seleccionado
 * Muestra información completa y opciones para ver historial/ruta
 */
const WorkerDetailsPanel = ({ worker, onClose, onViewHistory, onViewRoute }) => {
  if (!worker) return null;

  const getStatusColor = (estado) => {
    switch (estado) {
      case 'online':
        return 'success';
      case 'idle':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusText = (estado) => {
    switch (estado) {
      case 'online':
        return 'En línea';
      case 'idle':
        return 'Inactivo';
      default:
        return 'Desconectado';
    }
  };

  const formatLastUpdate = (segundos) => {
    if (segundos < 60) return 'Hace unos segundos';
    if (segundos < 3600) return `Hace ${Math.floor(segundos / 60)} min`;
    const hours = Math.floor(segundos / 3600);
    return `Hace ${hours} hora${hours > 1 ? 's' : ''}`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: 20,
        right: 20,
        width: 350,
        maxHeight: '80vh',
        overflow: 'auto',
        zIndex: 1000,
        borderRadius: 2
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" noWrap>
            {worker.nombre_completo || `${worker.nombre} ${worker.apellido}`}
          </Typography>
          <Typography variant="caption">
            {worker.username || 'Sin usuario asignado'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Estado */}
      <Box sx={{ p: 2 }}>
        <Chip
          label={getStatusText(worker.estado)}
          color={getStatusColor(worker.estado)}
          size="small"
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }} />

        {/* Información detallada */}
        <Grid container spacing={2}>
          {/* Velocidad */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SpeedIcon color="action" fontSize="small" />
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary">
                  Velocidad
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {worker.velocidad ? `${(worker.velocidad * 3.6).toFixed(1)} km/h` : 'Detenido'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Última actualización */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon color="action" fontSize="small" />
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary">
                  Última actualización
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatLastUpdate(worker.segundos_desde_actualizacion || 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatTimestamp(worker.timestamp_recepcion)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Ubicación GPS */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MyLocationIcon color="action" fontSize="small" />
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary">
                  Coordenadas GPS
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {parseFloat(worker.latitud)?.toFixed(6)}, {parseFloat(worker.longitud)?.toFixed(6)}
                </Typography>
                {worker.precision && (
                  <Typography variant="caption" color="text.secondary">
                    Precisión: ±{parseFloat(worker.precision).toFixed(1)}m
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Altitud */}
          {worker.altitud && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimelineIcon color="action" fontSize="small" />
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">
                    Altitud
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {parseFloat(worker.altitud).toFixed(1)} m
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Curso/Dirección */}
          {worker.curso !== null && worker.curso !== undefined && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RouteIcon color="action" fontSize="small" />
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">
                    Dirección
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {parseFloat(worker.curso).toFixed(0)}° {getCardinalDirection(parseFloat(worker.curso))}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Acciones */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Tooltip title="Ver historial de ubicaciones en un rango de fechas">
            <Button
              fullWidth
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => onViewHistory && onViewHistory(worker)}
            >
              Ver Historial
            </Button>
          </Tooltip>

          <Tooltip title="Ver ruta del trabajador en el mapa">
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RouteIcon />}
              onClick={() => onViewRoute && onViewRoute(worker)}
            >
              Ver Ruta
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

/**
 * Convertir grados a dirección cardinal
 */
function getCardinalDirection(degrees) {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(degrees / 45) % 8;
  return directions[index];
}

export default WorkerDetailsPanel;
